import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@gumahub/authentication';
import { MODULES } from '@gumahub/shared';

// Module color and icon configuration
const MODULE_CONFIG: Record<string, {
  gradient: string;
  bg: string;
  text: string;
  border: string;
  shadow: string;
  iconBg: string;
}> = {
  ENT: {
    gradient: 'from-guma-500 to-guma-600',
    bg: 'bg-guma-50',
    text: 'text-guma-600',
    border: 'border-guma-200',
    shadow: 'shadow-guma-500/10',
    iconBg: 'bg-guma-500'
  },
  ORD: {
    gradient: 'from-ocean-500 to-ocean-600',
    bg: 'bg-ocean-50',
    text: 'text-ocean-600',
    border: 'border-ocean-200',
    shadow: 'shadow-ocean-500/10',
    iconBg: 'bg-ocean-500'
  },
  SHP: {
    gradient: 'from-mint-500 to-mint-600',
    bg: 'bg-mint-50',
    text: 'text-mint-600',
    border: 'border-mint-200',
    shadow: 'shadow-mint-500/10',
    iconBg: 'bg-mint-500'
  },
  BID: {
    gradient: 'from-amber-400 to-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    border: 'border-amber-200',
    shadow: 'shadow-amber-500/10',
    iconBg: 'bg-amber-400'
  },
  EMP: {
    gradient: 'from-purple-500 to-purple-600',
    bg: 'bg-purple-50',
    text: 'text-purple-600',
    border: 'border-purple-200',
    shadow: 'shadow-purple-500/10',
    iconBg: 'bg-purple-500'
  },
  EXP: {
    gradient: 'from-pink-500 to-pink-600',
    bg: 'bg-pink-50',
    text: 'text-pink-600',
    border: 'border-pink-200',
    shadow: 'shadow-pink-500/10',
    iconBg: 'bg-pink-500'
  },
  TRV: {
    gradient: 'from-cyan-500 to-cyan-600',
    bg: 'bg-cyan-50',
    text: 'text-cyan-600',
    border: 'border-cyan-200',
    shadow: 'shadow-cyan-500/10',
    iconBg: 'bg-cyan-500'
  },
};

// Icons for modules
const ModuleIcons: Record<string, React.ReactNode> = {
  Building2: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  ShoppingCart: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  Truck: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  ),
  Gavel: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  Users: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Receipt: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
    </svg>
  ),
  Plane: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  ),
};

const getModuleIcon = (iconName: string): React.ReactNode => {
  return ModuleIcons[iconName] || ModuleIcons.Building2;
};

const DashboardPage: React.FC = () => {
  const { user, getAccessibleModules } = useAuth();
  const { t } = useTranslation('dashboard');
  const accessibleModules = getAccessibleModules();

  // Get current time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greeting.morning');
    if (hour < 17) return t('greeting.afternoon');
    return t('greeting.evening');
  };

  return (
    <div className="space-y-8">
      {/* Welcome header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500 dark:text-[#9aa0a6] mb-1">{getGreeting()}</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-[#e8eaed]">
            {t('welcome')}, <span className="text-guma-500 dark:text-[#ea8685]">{user?.displayName?.split(' ')[0]}</span>
          </h1>
          <p className="text-slate-500 dark:text-[#9aa0a6] mt-2 max-w-xl">
            {t('subtitle')}
          </p>
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#2d2d2d] rounded-xl border border-slate-200 dark:border-[#3c4043] shadow-sm">
            <div className="w-2 h-2 rounded-full bg-mint-500 dark:bg-[#81c995] animate-pulse" />
            <span className="text-sm font-medium text-slate-600 dark:text-[#9aa0a6]">{accessibleModules.length} {t('activeModules')}</span>
          </div>
          {user?.isPlatformAdmin && (
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-guma-500 to-guma-600 rounded-xl shadow-lg shadow-guma-500/20">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-semibold text-white">{t('platformAdmin')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Module cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {accessibleModules.map((moduleId, index) => {
          const module = MODULES[moduleId];
          if (!module) return null;

          const config = MODULE_CONFIG[moduleId] || MODULE_CONFIG.ENT;
          const moduleAccess = user?.modules[moduleId];
          const isAdmin = moduleAccess?.isAdmin;

          return (
            <Link
              key={moduleId}
              to={module.route}
              style={{ animationDelay: `${index * 75}ms` }}
              className="group animate-fade-in-up"
            >
              <div className={`relative overflow-hidden bg-white dark:bg-[#2d2d2d] rounded-2xl border border-slate-200 dark:border-[#3c4043] p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${config.shadow}`}>
                {/* Gradient accent line */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${config.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                {/* Background decoration */}
                <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full ${config.bg} opacity-50 group-hover:opacity-70 transition-opacity duration-300`} />

                <div className="relative">
                  {/* Header with icon */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${config.iconBg} flex items-center justify-center text-white shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                      {getModuleIcon(module.icon)}
                    </div>

                    {isAdmin && (
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg ${config.bg} ${config.text}`}>
                        Admin
                      </span>
                    )}
                  </div>

                  {/* Module info */}
                  <h3 className="text-lg font-bold text-slate-900 dark:text-[#e8eaed] mb-1 group-hover:text-slate-700 dark:group-hover:text-white transition-colors">
                    {module.moduleName}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-[#9aa0a6] line-clamp-2 mb-4">
                    {module.description}
                  </p>

                  {/* Roles */}
                  {moduleAccess && (
                    <div className="flex flex-wrap gap-1.5">
                      {moduleAccess.roles.slice(0, 2).map((role, index) => (
                        <span
                          key={`${moduleId}-role-${index}`}
                          className="px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-[#3c3c3c] text-slate-600 dark:text-[#9aa0a6] rounded-md"
                        >
                          {role.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {moduleAccess.roles.length > 2 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-[#3c3c3c] text-slate-600 dark:text-[#9aa0a6] rounded-md">
                          +{moduleAccess.roles.length - 2}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Arrow indicator */}
                  <div className={`absolute bottom-0 right-0 w-8 h-8 rounded-full ${config.bg} flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300`}>
                    <svg className={`w-4 h-4 ${config.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Account info card */}
      <div className="bg-white dark:bg-[#2d2d2d] rounded-2xl border border-slate-200 dark:border-[#3c4043] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-[#3c4043] bg-slate-50/50 dark:bg-[#1f1f1f]/50">
          <h2 className="text-lg font-bold text-slate-900 dark:text-[#e8eaed]">{t('account.title')}</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-400 dark:text-[#5f6368] uppercase tracking-wider">{t('account.username')}</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-[#e8eaed]">{user?.username}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-400 dark:text-[#5f6368] uppercase tracking-wider">{t('account.organization')}</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-[#e8eaed]">{user?.clientName}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-400 dark:text-[#5f6368] uppercase tracking-wider">{t('account.activeModules')}</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-[#e8eaed]">{accessibleModules.length} {t('account.modules')}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-400 dark:text-[#5f6368] uppercase tracking-wider">{t('account.status')}</p>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-mint-500 dark:bg-[#81c995]" />
                <p className="text-sm font-semibold text-mint-600 dark:text-[#81c995]">{t('account.active')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform highlights */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Red', value: 'Enterprise', color: 'bg-guma-500', textColor: 'text-guma-600' },
          { label: 'Yellow', value: 'Warning', color: 'bg-amber-400', textColor: 'text-amber-600' },
          { label: 'Green', value: 'Success', color: 'bg-mint-500', textColor: 'text-mint-600' },
          { label: 'Blue', value: 'Info', color: 'bg-ocean-500', textColor: 'text-ocean-600' },
        ].map((item, i) => (
          <div
            key={i}
            style={{ animationDelay: `${i * 100}ms` }}
            className="animate-fade-in-up bg-white dark:bg-[#2d2d2d] rounded-xl border border-slate-200 dark:border-[#3c4043] p-4 flex items-center gap-4"
          >
            <div className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center shadow-lg`}>
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-400 dark:text-[#5f6368] uppercase">{item.label}</p>
              <p className={`text-sm font-bold ${item.textColor}`}>{item.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
